import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import ArrowRight from '@untitled-ui/icons-react/build/cjs/ArrowRight';
import { UMButton, UMLoader } from '@urbanmetrix/um-kit';
import { useNavigate, ViewContext } from 'components/lib';
import ExitDialog from 'components/exit-dialog/ExitDialog';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { GeodataContext, type CloneGeodataSource } from 'contexts/GeodataContext';
import { GridContext } from 'contexts/GridContext';
import { ValidationContext } from 'contexts/ValidationContext';
import { getGeodata, getGeodatas, updateGeodata, uploadGeodataImportedFile } from 'api/geodataApi';
import { getCurrentUsage } from 'api/planApi';
import { getProgress } from 'api/progressApi';
import { getProject } from 'api/projectApi';
import { getGrid, getGridSize, replaceGrid } from 'api/gridApi';
import { isStorageLimitReached } from 'services/storageLimit';
import { normalizeProjectCoordinatesForMap, defaultCoordinateSystemPreference } from 'utils/coordinateSystemManager';
import { formatBytesToMbLabel } from 'utils/formatBytes';
import CadastralMapSection, { ReadOnlyAnalysisAreaCenterMarkerSync } from 'views/add-grid/map/CadastralMapSection';
import MapPreviewModeFrame, { VISUALIZER_PREVIEW_RELOAD_EVENT } from 'views/add-grid/map/MapPreviewModeFrame';
import { lv95ToWgs84ForOneCoordinate } from 'views/add-grid/map/coordinateTransforms';
import Style from 'views/add-grid/styles/grid.tailwind';
import Settings from 'settings.json';
import { crs2056, INITIAL_GRID_MAP_VIEWS, type GridMapType } from 'views/add-grid/map/mapConfig';
import L from 'leaflet';
import Terrain from './Terrain';
import Buildings from './Buildings';
import Trees from './Trees';
import UploadCustomObjects from './UploadCustomObjects';
import {
  buildGeodataBuildingImportUploads,
  buildGridGeodataPayload,
  buildGridGeodataReplacePayload,
  persistDraftGeodata,
  type GeodataCloneSelections,
  type GeodataCloneModuleSelection,
} from './persistDraftGeodata';
import './styles/geodata.css';
import ProjectCreationShell from 'views/project-creation/ProjectCreationShell';
import ProjectCreationSplitLayout from 'views/project-creation/ProjectCreationSplitLayout';
import type { ProjectCreationPreviewState } from 'views/project-creation/ProjectCreationPersistentPreview';
import usePreviewSlotRectReporter from 'views/project-creation/usePreviewSlotRectReporter';
import ReviewHeader from 'views/project-page/components/ReviewHeader';
import BackArrow from 'components/back-arrow/BackArrow';
import { buildGridParamsPayload, type RawGridParams } from 'views/add-grid/gridPayload';
import { enrichGridParamsPayloadWithHeight } from 'services/gridHeightEnrichment';
import type { GridInsert } from '@urbanmetrix/sdk';
import {
  hasGeodataNumericValidationErrors,
  isConcaveAlphaValueValid,
  isFarTerrainLengthValid,
  isIntermediateTerrainLengthValid,
  isTreesDonutRadiusValid,
  isTreesMinimalHeightValid,
  isTreesRadiusValid,
} from './geodataNumericValidation';
import { getApiErrorMessage } from 'api/apiClient';
import { getGeodataModuleLimitMessage, getGeodataRequiredModuleCount } from 'services/geodataUsage';

interface AddGeodataProps {
  reviewMode?: boolean;
  reviewGeodataId?: string | null;
  breadcrumbTitle?: ReactNode;
  gridIdOverride?: string | null;
  previewLoading?: boolean;
  onUpdatePreview?: (gridId: string) => void | Promise<void>;
  onCloseOverride?: (geodataId?: string | null) => void;
  onExitOverride?: () => void;
  onPreviewStateChange?: (preview: ProjectCreationPreviewState | null) => void;
  onPreviewSlotRectChange?: (rect: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'> | null) => void;
  previewRectAnchor?: HTMLElement | null;
}

type ProjectData = {
  id?: string | number;
  coordinates?: { lat?: number | null; lng?: number | null } | null;
  geodatasAvailableCount?: number | null;
  [key: string]: unknown;
};

type AccordionSectionKey = 'terrain' | 'buildings' | 'trees' | 'uploads';
type CloneModuleKey = 'terrain' | 'buildings' | 'trees';

const GEODATA_BUSY_STATUSES = new Set(['INITIALIZING', 'RUNNING']);
const GEODATA_POLLING_STOP_STATUSES = new Set(['SUCCEEDED']);
const GEODATA_FAILURE_STATUSES = new Set(['FAILED']);
const GEODATA_FAILURE_MESSAGE =
  'Preview update failed. You can keep editing and try Update Preview again when the issue is resolved.';
const getGeodataPollingDelayMs = (elapsedMs: number) => {
  if (elapsedMs < 5_000) return 1_000;
  if (elapsedMs < 15_000) return 2_000;
  if (elapsedMs < 25_000) return 3_000;
  if (elapsedMs < 45_000) return 5_000;
  return 10_000;
};

const SECTION_LABELS: Record<AccordionSectionKey, string> = {
  terrain: 'Terrain',
  buildings: 'Buildings',
  trees: 'Trees',
  uploads: 'Upload Custom Objects',
};

const CLONE_MODULE_LABELS: Record<CloneModuleKey, string> = {
  terrain: 'Terrain',
  buildings: 'Buildings',
  trees: 'Trees',
};

const uploadCustomObjectsTooltip = (
  <>
    Learn how to import OBJ files in our{' '}
    <Link
      href="https://urbanmetrix.com/guide/set-up-a-new-project/upload-obj-files"
      target="_blank"
      rel="noreferrer"
      underline="always"
      color="inherit"
      sx={{ cursor: 'pointer' }}
    >
      User Guide
    </Link>
  </>
);

const sumSizeValues = (values: ReadonlyArray<number | null | undefined>): number =>
  values.reduce<number>((total, value) => total + (Number.isFinite(Number(value)) ? Number(value) : 0), 0);

const toFiniteNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeProjectForMap = (projectData: Record<string, unknown> | null | undefined): ProjectData | null => {
  if (!projectData) {
    return null;
  }

  return normalizeProjectCoordinatesForMap({
    ...projectData,
    coordinates: ((projectData as { projectCoordinates?: unknown }).projectCoordinates ??
      (projectData as { coordinates?: unknown }).coordinates ??
      undefined) as ProjectData['coordinates'],
  } as ProjectData) as ProjectData | null;
};

const buildCloneAvailability = (geodata: {
  clonedFromGeodataId?: string | null;
  cloneSourceStatus?: string | null;
  cloneSourceProgressStatus?: string | null;
  cloneSourceHasTerrain?: boolean;
  cloneSourceHasBuildings?: boolean;
  cloneSourceHasTrees?: boolean;
  cloneSourceHasTargets?: boolean;
}) => {
  const sourceStatus = geodata.cloneSourceProgressStatus ?? geodata.cloneSourceStatus ?? null;

  return {
    isCloneFlow: Boolean(geodata.clonedFromGeodataId),
    sourceSucceeded: sourceStatus === 'SUCCEEDED',
    hasTerrain: Boolean(geodata.cloneSourceHasTerrain),
    hasBuildings: Boolean(geodata.cloneSourceHasBuildings),
    hasTrees: Boolean(geodata.cloneSourceHasTrees),
    hasTargets: Boolean(geodata.cloneSourceHasTargets),
  };
};

const buildDefaultCloneSelections = (
  availability: ReturnType<typeof buildCloneAvailability>
): GeodataCloneSelections => ({
  terrain: { enabled: true, mode: 'rerun' },
  buildings: { enabled: availability.hasBuildings, mode: 'rerun' },
  trees: { enabled: availability.hasTrees, mode: 'rerun' },
});

const AddGeodata = ({
  reviewMode = false,
  reviewGeodataId = null,
  breadcrumbTitle,
  gridIdOverride = null,
  previewLoading = false,
  onUpdatePreview,
  onCloseOverride,
  onExitOverride,
  onPreviewStateChange,
  onPreviewSlotRectChange,
  previewRectAnchor = null,
}: AddGeodataProps) => {
  const navigate = useNavigate();
  const viewContext = useContext(ViewContext);
  const location = useLocation();
  const params = useParams();
  const gridIdFromRoute = gridIdOverride ?? params.gridId ?? null;
  const geodataIdFromQuery = new URLSearchParams(location.search).get('geodataId');
  const routeGeodataId = params.geodataId;
  const resolvedReviewGeodataId = reviewGeodataId ?? routeGeodataId;
  const isReadOnlyReview = Boolean(reviewMode || resolvedReviewGeodataId);

  const { currentProject } = useContext(CurrentProjectContext);
  const {
    geodata,
    geodataParams,
    setGeodata,
    clearGeodataFormData,
    cloneGeodataData,
    setIsCloningGeodata,
    isCloningGeodata,
  } = useContext(GeodataContext);
  const { grid, gridParams, cloneGridData } = useContext(GridContext);
  const { isGridCoordinateSystemValid } = useContext(ValidationContext);

  const [gridId, setGridId] = useState<string | null>(gridIdFromRoute ?? null);
  const [draftGeodataId, setDraftGeodataId] = useState<string | null>(resolvedReviewGeodataId ?? null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [project, setProject] = useState<ProjectData | null>(null);
  const [geodataSizeLabel, setGeodataSizeLabel] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<AccordionSectionKey | null>(null);
  const [isStorageLimitReachedForImports, setIsStorageLimitReachedForImports] = useState(false);
  const [mapType, setMapType] = useState<GridMapType>('cadastral');
  const [mapViews, setMapViews] = useState(INITIAL_GRID_MAP_VIEWS);
  const [isSaving, setIsSaving] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [confirmedExitPath, setConfirmedExitPath] = useState<string | null>(null);
  const [cloneSelections, setCloneSelections] = useState<GeodataCloneSelections | null>(null);
  const [isStandaloneGridLoaded, setIsStandaloneGridLoaded] = useState(false);
  const [isStandaloneNameLoaded, setIsStandaloneNameLoaded] = useState(false);
  const [isStandaloneGeodataLoaded, setIsStandaloneGeodataLoaded] = useState(false);
  const [lastSavedPreviewSignature, setLastSavedPreviewSignature] = useState<string | null>(null);
  const [standaloneGeodataStatus, setStandaloneGeodataStatus] = useState<string | null>(null);
  const [isStandaloneGeodataPollingEnabled, setIsStandaloneGeodataPollingEnabled] = useState(false);
  const cloneAvailability = useMemo(() => buildCloneAvailability(geodata), [geodata]);
  const isCloneFlow = !isReadOnlyReview && isCloningGeodata && cloneAvailability.isCloneFlow;
  const isProjectCreationFlow = !isReadOnlyReview && typeof onCloseOverride === 'function';
  const currentPreviewSignature = useMemo(
    () =>
      JSON.stringify({
        grid: buildGridParamsPayload(gridParams as RawGridParams),
        geodata: buildGridGeodataPayload(geodata, geodataParams),
      }),
    [geodata, geodataParams, gridParams]
  );
  const hasGeodataInputValidationError = useMemo(
    () =>
      hasGeodataNumericValidationErrors({
        treesEnabled: Boolean(geodata?.treesEnabled),
        lengthMid: geodataParams?.lengthMid,
        lengthFar: geodataParams?.lengthFar,
        resolutionFar: geodataParams?.resolutionFar,
        treesRadius: geodataParams?.treesRadius,
        treesDonutRadius: geodataParams?.treesDonutRadius,
        treesMinimalHeight: geodataParams?.treesMinimalHeight,
        concaveAlphaValue: geodataParams?.concaveAlphaValue,
      }) || !isGridCoordinateSystemValid,
    [
      geodata?.treesEnabled,
      geodataParams?.concaveAlphaValue,
      geodataParams?.lengthFar,
      geodataParams?.lengthMid,
      geodataParams?.resolutionFar,
      geodataParams?.treesDonutRadius,
      geodataParams?.treesMinimalHeight,
      geodataParams?.treesRadius,
      isGridCoordinateSystemValid,
    ]
  );
  const sectionHasError = useMemo<Record<AccordionSectionKey, boolean>>(
    () => ({
      terrain:
        !isIntermediateTerrainLengthValid(geodataParams?.lengthMid) ||
        !isFarTerrainLengthValid(geodataParams?.lengthFar, geodataParams?.resolutionFar),
      buildings: false,
      trees:
        Boolean(geodata?.treesEnabled) &&
        (!isTreesRadiusValid(geodataParams?.treesRadius) ||
          !isTreesDonutRadiusValid(geodataParams?.treesDonutRadius) ||
          !isTreesMinimalHeightValid(geodataParams?.treesMinimalHeight) ||
          !isConcaveAlphaValueValid(geodataParams?.concaveAlphaValue)),
      uploads: !isGridCoordinateSystemValid,
    }),
    [
      geodata?.treesEnabled,
      geodataParams?.concaveAlphaValue,
      geodataParams?.lengthFar,
      geodataParams?.lengthMid,
      geodataParams?.resolutionFar,
      geodataParams?.treesDonutRadius,
      geodataParams?.treesMinimalHeight,
      geodataParams?.treesRadius,
      isGridCoordinateSystemValid,
    ]
  );

  const legalBorderRef = useRef<L.Polygon | null>(null);
  const buildableBorderRef = useRef<L.Polygon | null>(null);
  const analysisAreaRef = useRef<L.Polygon | null>(null);
  const analysisAreaInactiveRefs = useRef<L.Polygon[]>([]);
  const isDrawingRef = useRef(false);
  const previewSlotRef = useRef<HTMLDivElement | null>(null);
  const usesExternalPreview = isProjectCreationFlow && typeof onPreviewStateChange === 'function';
  const notifiedFailureStatusRef = useRef<string | null>(null);
  const loadedStandaloneGeodataIdRef = useRef<string | null>(null);

  const handleStandaloneGeodataStatus = useCallback(
    (nextStatus: string | null) => {
      setStandaloneGeodataStatus(nextStatus);
      if (!nextStatus || !GEODATA_FAILURE_STATUSES.has(nextStatus)) {
        notifiedFailureStatusRef.current = null;
      }

      if (nextStatus && GEODATA_BUSY_STATUSES.has(nextStatus)) {
        setIsStandaloneGeodataPollingEnabled(true);
        return;
      }

      setIsStandaloneGeodataPollingEnabled(false);
      if (nextStatus && GEODATA_FAILURE_STATUSES.has(nextStatus) && notifiedFailureStatusRef.current !== nextStatus) {
        notifiedFailureStatusRef.current = nextStatus;
        viewContext.notification.show(GEODATA_FAILURE_MESSAGE, 'error', true, 'toast');
      }
    },
    [viewContext.notification]
  );

  useEffect(() => {
    if (isReadOnlyReview) {
      return;
    }

    if (isProjectCreationFlow) {
      return;
    }

    if (isCloningGeodata || geodataIdFromQuery) {
      return;
    }

    clearGeodataFormData();
    setGeodata((prev) => ({
      ...prev,
      terrainEnabled: true,
      buildingsEnabled: true,
      treesEnabled: true,
    }));
  }, [clearGeodataFormData, geodataIdFromQuery, isCloningGeodata, isProjectCreationFlow, isReadOnlyReview, setGeodata]);

  useEffect(() => {
    if (!isCloneFlow) {
      setCloneSelections(null);
      return;
    }

    setCloneSelections((prev) => prev ?? buildDefaultCloneSelections(cloneAvailability));
  }, [cloneAvailability, isCloneFlow]);

  useEffect(() => {
    if (!isReadOnlyReview) {
      setGridId(gridIdFromRoute ?? null);
      if (!geodataIdFromQuery) setDraftGeodataId(null);
      return;
    }
    if (!resolvedReviewGeodataId) return;

    let cancelled = false;

    const loadGeodata = async () => {
      try {
        const geodataData = await getGeodata(String(resolvedReviewGeodataId));
        if (cancelled || !geodataData) return;

        cloneGeodataData(geodataData as CloneGeodataSource, { appendClonedSuffix: false });
        setIsCloningGeodata(true);
        setGridId(geodataData.gridId ?? null);
        setDraftGeodataId(geodataData.id == null ? null : String(geodataData.id));
      } catch (error) {
        console.error('Error fetching geodata:', error);
      }
    };

    loadGeodata();

    return () => {
      cancelled = true;
    };
  }, [
    cloneGeodataData,
    geodataIdFromQuery,
    gridIdFromRoute,
    isReadOnlyReview,
    resolvedReviewGeodataId,
    setIsCloningGeodata,
  ]);

  useEffect(() => {
    if (isReadOnlyReview || isProjectCreationFlow) return;
    if (!geodataIdFromQuery) {
      loadedStandaloneGeodataIdRef.current = null;
      setIsStandaloneGeodataLoaded(true);
      return;
    }
    if (loadedStandaloneGeodataIdRef.current === geodataIdFromQuery) return;

    let cancelled = false;
    setIsStandaloneGeodataLoaded(false);

    const loadDraftGeodata = async () => {
      try {
        const geodataData = await getGeodata(geodataIdFromQuery);
        if (cancelled || !geodataData) return;
        if (String(geodataData.gridId) !== String(gridIdFromRoute)) {
          throw new Error('Draft geodata does not belong to this grid.');
        }

        cloneGeodataData(geodataData as CloneGeodataSource, { appendClonedSuffix: false });
        setIsCloningGeodata(false);
        setDraftGeodataId(String(geodataData.id));
        loadedStandaloneGeodataIdRef.current = geodataIdFromQuery;
        handleStandaloneGeodataStatus(
          typeof geodataData.progressStatus === 'string' ? geodataData.progressStatus : null
        );
      } catch (error) {
        console.error('Error fetching draft geodata:', error);
        setDraftGeodataId(null);
      } finally {
        if (!cancelled) setIsStandaloneGeodataLoaded(true);
      }
    };

    void loadDraftGeodata();
    return () => {
      cancelled = true;
    };
  }, [
    cloneGeodataData,
    geodataIdFromQuery,
    gridIdFromRoute,
    handleStandaloneGeodataStatus,
    isProjectCreationFlow,
    isReadOnlyReview,
    setIsCloningGeodata,
  ]);

  useEffect(() => {
    if (isProjectCreationFlow) {
      setProjectId(currentProject?.id != null ? String(currentProject.id) : null);
      return;
    }

    let cancelled = false;
    setIsStandaloneGridLoaded(false);

    const loadGrid = async () => {
      if (!gridId) return;

      try {
        const gridData = await getGrid(String(gridId));
        if (cancelled || !gridData) return;

        cloneGridData(gridData, { appendClonedSuffix: false });
        setProjectId(gridData.projectId ?? null);
      } catch (error) {
        console.error('Error fetching grid:', error);
      } finally {
        if (!cancelled) setIsStandaloneGridLoaded(true);
      }
    };

    loadGrid();

    return () => {
      cancelled = true;
    };
  }, [cloneGridData, currentProject?.id, gridId, isProjectCreationFlow]);

  useEffect(() => {
    let cancelled = false;

    if (isProjectCreationFlow) {
      setProject(currentProject ? normalizeProjectForMap(currentProject as Record<string, unknown>) : null);
      if (currentProject?.id != null) {
        setProjectId(String(currentProject.id));
      }
      return () => {
        cancelled = true;
      };
    }

    const matchingCurrentProject =
      currentProject && projectId && String(currentProject.id) === String(projectId) ? currentProject : null;

    if (matchingCurrentProject) {
      setProject(normalizeProjectForMap(matchingCurrentProject as Record<string, unknown>));
      return () => {
        cancelled = true;
      };
    }

    const loadProject = async () => {
      if (!projectId) return;

      try {
        const projectData = await getProject(String(projectId));
        if (cancelled || !projectData) return;

        setProject(normalizeProjectForMap(projectData as Record<string, unknown>));
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };

    loadProject();

    return () => {
      cancelled = true;
    };
  }, [currentProject, isProjectCreationFlow, projectId]);

  useEffect(() => {
    if (!isReadOnlyReview || !gridId || !resolvedReviewGeodataId) {
      setGeodataSizeLabel(null);
      return;
    }

    let cancelled = false;

    const loadGeodataSize = async () => {
      try {
        const sizePayload = await getGridSize(String(gridId));
        if (cancelled) return;

        const geodataSize = sizePayload?.grids
          ?.flatMap((gridItem) => gridItem.geodatas ?? [])
          .find((geodataItem) => String(geodataItem.id) === String(resolvedReviewGeodataId));

        const totalSize = geodataSize
          ? sumSizeValues([
              geodataSize.buildingsSize,
              geodataSize.roadsSize,
              geodataSize.vegetationSize,
              geodataSize.groundSize,
            ])
          : null;

        setGeodataSizeLabel(formatBytesToMbLabel(totalSize) ?? '-');
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching review geodata size:', error);
        setGeodataSizeLabel('-');
      }
    };

    loadGeodataSize();

    return () => {
      cancelled = true;
    };
  }, [gridId, isReadOnlyReview, resolvedReviewGeodataId]);

  useEffect(() => {
    if (isReadOnlyReview || !gridId || isCloneFlow || isProjectCreationFlow) return;

    let cancelled = false;
    setIsStandaloneNameLoaded(false);

    const loadGeodataName = async () => {
      try {
        const geodataList = await getGeodatas({ gridId: String(gridId) });
        if (cancelled) return;

        const nextName = `Geodata ${(Array.isArray(geodataList) ? geodataList.length : 0) + 1}`;
        setGeodata((prev) => ({
          ...prev,
          name: prev.name || nextName,
          description: prev.description || '',
        }));
      } catch (error) {
        console.error('Error fetching geodata list:', error);
      } finally {
        if (!cancelled) setIsStandaloneNameLoaded(true);
      }
    };

    loadGeodataName();

    return () => {
      cancelled = true;
    };
  }, [gridId, isCloneFlow, isProjectCreationFlow, isReadOnlyReview, setGeodata]);

  useEffect(() => {
    if (
      isReadOnlyReview ||
      isProjectCreationFlow ||
      !isStandaloneGridLoaded ||
      !isStandaloneNameLoaded ||
      !isStandaloneGeodataLoaded ||
      lastSavedPreviewSignature !== null
    ) {
      return;
    }

    setLastSavedPreviewSignature(currentPreviewSignature);
  }, [
    currentPreviewSignature,
    isProjectCreationFlow,
    isReadOnlyReview,
    isStandaloneGridLoaded,
    isStandaloneGeodataLoaded,
    isStandaloneNameLoaded,
    lastSavedPreviewSignature,
  ]);

  useEffect(() => {
    if (isProjectCreationFlow || !draftGeodataId || !isStandaloneGeodataPollingEnabled) return;

    let cancelled = false;
    let timeoutId: number | null = null;
    const startedAt = Date.now();

    const scheduleNextPoll = () => {
      if (cancelled) return;
      timeoutId = window.setTimeout(() => void pollStatus(), getGeodataPollingDelayMs(Date.now() - startedAt));
    };

    const pollStatus = async () => {
      try {
        const progress = await getProgress({ geodataIds: [String(draftGeodataId)] });
        if (cancelled) return;

        const payload = progress?.geodatas?.[String(draftGeodataId)] ?? null;
        const nextStatus = typeof payload?.state === 'string' ? payload.state : standaloneGeodataStatus;

        setStandaloneGeodataStatus(nextStatus);
        if (!nextStatus || !GEODATA_FAILURE_STATUSES.has(nextStatus)) {
          notifiedFailureStatusRef.current = null;
        }
        if (nextStatus && GEODATA_POLLING_STOP_STATUSES.has(nextStatus)) {
          window.dispatchEvent(new Event(VISUALIZER_PREVIEW_RELOAD_EVENT));
          setIsStandaloneGeodataPollingEnabled(false);
          return;
        }
        if (nextStatus && GEODATA_FAILURE_STATUSES.has(nextStatus)) {
          setIsStandaloneGeodataPollingEnabled(false);
          if (notifiedFailureStatusRef.current !== nextStatus) {
            notifiedFailureStatusRef.current = nextStatus;
            viewContext.notification.show(GEODATA_FAILURE_MESSAGE, 'error', true, 'toast');
          }
          return;
        }
      } catch (error) {
        if (!cancelled) console.error('Error polling standalone geodata progress:', error);
      }

      scheduleNextPoll();
    };

    void pollStatus();

    return () => {
      cancelled = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [
    draftGeodataId,
    isProjectCreationFlow,
    isStandaloneGeodataPollingEnabled,
    standaloneGeodataStatus,
    viewContext.notification,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadUsage = async () => {
      try {
        const usage = await getCurrentUsage();
        if (!cancelled) {
          setIsStorageLimitReachedForImports(isStorageLimitReached(usage));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching storage usage for geodata settings:', error);
          setIsStorageLimitReachedForImports(false);
        }
      }
    };

    loadUsage();

    return () => {
      cancelled = true;
    };
  }, []);

  const mapCenter = useMemo<[number, number]>(() => {
    if (project?.coordinates?.lat != null && project?.coordinates?.lng != null) {
      return [Number(project.coordinates.lat), Number(project.coordinates.lng)];
    }

    const manualCenterX = toFiniteNumber(gridParams?.manualCenter?.x);
    const manualCenterY = toFiniteNumber(gridParams?.manualCenter?.y);
    if (manualCenterX != null && manualCenterY != null && (manualCenterX !== 0 || manualCenterY !== 0)) {
      const center = lv95ToWgs84ForOneCoordinate(manualCenterX, manualCenterY);
      return [center.lat, center.lng];
    }

    const analysisArea = Array.isArray(gridParams?.analysisArea) ? gridParams.analysisArea : [];
    const validPoints = analysisArea
      .map((point) => ({
        x: toFiniteNumber(point?.x),
        y: toFiniteNumber(point?.y),
      }))
      .filter((point): point is { x: number; y: number } => point.x != null && point.y != null);

    if (validPoints.length > 0) {
      const centerX = validPoints.reduce((sum, point) => sum + point.x, 0) / validPoints.length;
      const centerY = validPoints.reduce((sum, point) => sum + point.y, 0) / validPoints.length;
      const center = lv95ToWgs84ForOneCoordinate(centerX, centerY);
      return [center.lat, center.lng];
    }

    return [0, 0];
  }, [
    gridParams?.analysisArea,
    gridParams?.manualCenter?.x,
    gridParams?.manualCenter?.y,
    project?.coordinates?.lat,
    project?.coordinates?.lng,
  ]);

  useEffect(() => {
    if (mapCenter[0] === 0 || mapCenter[1] === 0) return;

    setMapViews((prev) => ({
      cadastral: { ...prev.cadastral, center: mapCenter },
      osm: { ...prev.osm, center: mapCenter },
    }));
  }, [mapCenter]);

  useEffect(() => {
    if (!usesExternalPreview) {
      onPreviewStateChange?.(null);
      return;
    }

    onPreviewStateChange({
      gridId,
      isPreviewLoading: previewLoading,
      onUpdatePreview: gridId && typeof onUpdatePreview === 'function' ? () => onUpdatePreview(String(gridId)) : null,
      hasValidationErrors: hasGeodataInputValidationError,
      mapCenter,
      gridParams,
    });
  }, [
    gridId,
    gridParams,
    hasGeodataInputValidationError,
    mapCenter,
    onPreviewStateChange,
    onUpdatePreview,
    previewLoading,
    usesExternalPreview,
  ]);

  usePreviewSlotRectReporter({
    enabled: usesExternalPreview,
    element: previewSlotRef.current,
    relativeTo: previewRectAnchor,
    onRectChange: onPreviewSlotRectChange,
  });

  const exitPath = projectId ? `/projects/${projectId}` : '/dashboard';

  useEffect(() => {
    if (confirmedExitPath && location.pathname === confirmedExitPath) {
      setConfirmedExitPath(null);
    }
  }, [confirmedExitPath, location.pathname]);

  const handleExit = useCallback(() => {
    if (typeof onExitOverride === 'function') {
      onExitOverride();
      return;
    }

    setConfirmedExitPath(exitPath);
  }, [exitPath, onExitOverride]);

  const handleCloseToGridSettings = useCallback(
    (nextGeodataId?: string | null) => {
      if (typeof onCloseOverride === 'function') {
        onCloseOverride(nextGeodataId);
        return;
      }

      if (window.history.length > 1) {
        window.history.back();
        return;
      }

      if (projectId) {
        navigate(`/grids/add/${projectId}#2`);
        return;
      }

      const currentProjectId = currentProject?.id;

      if (!currentProjectId) {
        navigate('/dashboard');
        return;
      }

      navigate(`/projects/${currentProjectId}`);
    },
    [currentProject?.id, navigate, onCloseOverride, projectId]
  );

  const handleSectionToggle = (section: AccordionSectionKey) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const handleCloneSelectionToggle = useCallback(
    (module: CloneModuleKey, checked: boolean) => {
      setCloneSelections((prev) => {
        const nextSelections = prev ?? buildDefaultCloneSelections(cloneAvailability);
        return {
          ...nextSelections,
          [module]: {
            ...nextSelections[module],
            enabled: checked,
          },
        };
      });

      if (module === 'buildings') {
        setGeodata((prev) => ({ ...prev, buildingsEnabled: checked }));
      }

      if (module === 'trees') {
        setGeodata((prev) => ({ ...prev, treesEnabled: checked }));
      }
    },
    [cloneAvailability, setGeodata]
  );

  const handleCloneModeChange = useCallback(
    (module: CloneModuleKey, mode: GeodataCloneModuleSelection['mode']) => {
      setCloneSelections((prev) => {
        const nextSelections = prev ?? buildDefaultCloneSelections(cloneAvailability);
        return {
          ...nextSelections,
          [module]: {
            ...nextSelections[module],
            mode,
          },
        };
      });

      if (module === 'buildings') {
        setGeodata((prev) => ({ ...prev, buildingsEnabled: mode === 'rerun' }));
      }

      if (module === 'trees') {
        setGeodata((prev) => ({ ...prev, treesEnabled: mode === 'rerun' }));
      }
    },
    [cloneAvailability, setGeodata]
  );

  const saveDraftGeodata = useCallback(async () => {
    if (isReadOnlyReview || !gridId) {
      return;
    }

    if (isCloneFlow || (!draftGeodataId && !isProjectCreationFlow)) {
      const remainingGeodatas = toFiniteNumber(project?.geodatasAvailableCount);
      const geodataLimitMessage = getGeodataModuleLimitMessage(
        remainingGeodatas,
        getGeodataRequiredModuleCount(geodata)
      );
      if (geodataLimitMessage) throw new Error(geodataLimitMessage);
    }

    if (isProjectCreationFlow && !isCloneFlow) {
      const gridPayloadParams = (await enrichGridParamsPayloadWithHeight(
        buildGridParamsPayload(gridParams as RawGridParams)
      )) as NonNullable<GridInsert['params']>;

      await replaceGrid(String(gridId), {
        name: grid.name || 'Grid 1',
        description: grid.description || null,
        params: gridPayloadParams,
        geodata: buildGridGeodataReplacePayload(geodata, geodataParams),
      });
    }

    if (isProjectCreationFlow && !isCloneFlow) {
      const importedFileUploads = buildGeodataBuildingImportUploads(geodataParams);
      if (draftGeodataId && importedFileUploads.length > 0) {
        await Promise.all(
          importedFileUploads.map(({ kind, file }) => uploadGeodataImportedFile(String(draftGeodataId), kind, file))
        );
        const refreshedGrid = await getGrid(String(gridId));
        if (refreshedGrid) cloneGridData(refreshedGrid, { appendClonedSuffix: false });
      }
      return draftGeodataId ? { id: draftGeodataId } : null;
    }

    const persistedGeodata = await persistDraftGeodata({
      gridId: String(gridId),
      draftGeodataId: isCloneFlow ? null : draftGeodataId,
      geodata,
      geodataParams,
      cloneSourceGeodataId: isCloneFlow ? geodata.clonedFromGeodataId : null,
      cloneSelections: isCloneFlow ? cloneSelections : null,
    });

    const persistedGeodataId = persistedGeodata?.id == null ? draftGeodataId : String(persistedGeodata.id);
    const importedFileUploads = buildGeodataBuildingImportUploads(geodataParams);
    if (persistedGeodataId && importedFileUploads.length > 0) {
      await Promise.all(
        importedFileUploads.map(({ kind, file }) => uploadGeodataImportedFile(persistedGeodataId, kind, file))
      );
      const refreshedGrid = await getGrid(String(gridId));
      if (refreshedGrid) cloneGridData(refreshedGrid, { appendClonedSuffix: false });
    }

    setDraftGeodataId(persistedGeodataId);
    if (!isProjectCreationFlow && persistedGeodataId && persistedGeodataId !== geodataIdFromQuery) {
      const nextParams = new URLSearchParams(location.search);
      nextParams.set('geodataId', persistedGeodataId);
      navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
    }
    const persistedProgressStatus =
      typeof (persistedGeodata as { progressStatus?: unknown })?.progressStatus === 'string'
        ? ((persistedGeodata as { progressStatus: string }).progressStatus ?? null)
        : null;
    handleStandaloneGeodataStatus(persistedProgressStatus);
    return persistedGeodata;
  }, [
    cloneSelections,
    draftGeodataId,
    geodata,
    geodata.clonedFromGeodataId,
    geodataParams,
    grid,
    grid.description,
    gridId,
    grid.name,
    gridParams,
    isCloneFlow,
    isProjectCreationFlow,
    isReadOnlyReview,
    project?.geodatasAvailableCount,
    cloneGridData,
    handleStandaloneGeodataStatus,
    geodataIdFromQuery,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleUpdateStandalonePreview = useCallback(async () => {
    const persistedGeodata = await saveDraftGeodata();
    if (!persistedGeodata) return;

    setLastSavedPreviewSignature(currentPreviewSignature);
    const progressStatus = (persistedGeodata as { progressStatus?: unknown }).progressStatus;
    if (typeof progressStatus !== 'string' || !GEODATA_BUSY_STATUSES.has(progressStatus)) {
      window.dispatchEvent(new Event(VISUALIZER_PREVIEW_RELOAD_EVENT));
    }
  }, [currentPreviewSignature, saveDraftGeodata]);

  const handleSaveGeodata = useCallback(async () => {
    if (hasGeodataInputValidationError) {
      return;
    }

    if (isReadOnlyReview || !gridId) {
      handleCloseToGridSettings();
      return;
    }

    if (isProjectCreationFlow) {
      handleCloseToGridSettings(draftGeodataId);
      return;
    }

    try {
      setIsSaving(true);
      const persistedGeodata = await saveDraftGeodata();
      const nextGeodataId = persistedGeodata?.id == null ? draftGeodataId : String(persistedGeodata.id);
      handleCloseToGridSettings(nextGeodataId);
    } catch (error) {
      console.error('Error saving geodata settings:', error);
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to save geodata settings right now.'),
        'error',
        true,
        'toast'
      );
      setIsSaving(false);
    }
  }, [
    draftGeodataId,
    gridId,
    handleCloseToGridSettings,
    hasGeodataInputValidationError,
    isProjectCreationFlow,
    isReadOnlyReview,
    saveDraftGeodata,
    viewContext.notification,
  ]);

  const handleSaveAndExit = useCallback(async () => {
    if (isReadOnlyReview || !gridId) {
      setExitModalOpen(false);
      handleExit();
      return;
    }

    if (isProjectCreationFlow) {
      setExitModalOpen(false);
      handleExit();
      return;
    }

    try {
      setIsSaving(true);
      await saveDraftGeodata();
      setExitModalOpen(false);
      handleExit();
    } catch (error) {
      console.error('Error saving geodata settings before exit:', error);
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to save geodata settings right now.'),
        'error',
        true,
        'toast'
      );
      setIsSaving(false);
    }
  }, [gridId, handleExit, isReadOnlyReview, saveDraftGeodata, viewContext.notification]);

  const sections: AccordionSectionKey[] = ['terrain', 'buildings', 'trees', 'uploads'];
  const visibleSections = useMemo(() => {
    if (!isCloneFlow || !cloneSelections) {
      return sections;
    }

    const nextSections: AccordionSectionKey[] = [];

    if (cloneSelections.terrain.mode === 'rerun') {
      nextSections.push('terrain');
    }
    if (cloneSelections.buildings.enabled && cloneSelections.buildings.mode === 'rerun') {
      nextSections.push('buildings');
    }
    if (cloneSelections.trees.enabled && cloneSelections.trees.mode === 'rerun') {
      nextSections.push('trees');
    }

    nextSections.push('uploads');

    return nextSections;
  }, [cloneSelections, isCloneFlow]);

  if (confirmedExitPath && location.pathname !== confirmedExitPath) {
    return <Navigate to={confirmedExitPath} replace />;
  }

  if (
    !isProjectCreationFlow &&
    (!project || mapCenter[0] === 0 || mapCenter[1] === 0 || (geodataIdFromQuery && !isStandaloneGeodataLoaded))
  ) {
    return (
      <div className="review-loader-center">
        <UMLoader />
      </div>
    );
  }

  return (
    <>
      <ProjectCreationShell
        title={breadcrumbTitle ?? 'Grid Settings > Geodata Settings'}
        onClose={isReadOnlyReview ? undefined : () => setExitModalOpen(true)}
        closeDisabled={isProjectCreationFlow && hasGeodataInputValidationError}
        hideCloseButton={!isReadOnlyReview && isProjectCreationFlow}
        hideDefaultHeader={isReadOnlyReview}
        customHeader={
          isReadOnlyReview ? (
            <ReviewHeader
              name={geodata?.name}
              description={geodata?.description}
              sizeLabel={geodataSizeLabel}
              showSize
              item={geodata ?? {}}
              onEditDescription={(value) => {
                setGeodata((prev) => ({ ...prev, description: value }));
              }}
              onSaveDescription={(value) => updateGeodata(String(resolvedReviewGeodataId), { description: value })}
              onClose={handleExit}
            />
          ) : null
        }
        footerLeft={null}
      >
        <div className="grid-analysis-step">
          <ProjectCreationSplitLayout
            className="grid-settings-step geodata-settings-page__body"
            leftClassName="grid-settings-step__sidebar geodata-settings-page__sidebar"
            rightClassName={`grid-settings-step__preview grid-settings-step__preview--project geodata-settings-page__preview${
              usesExternalPreview ? ' grid-settings-step__preview--external-host' : ''
            }`}
            left={
              <>
                {!isReadOnlyReview && isProjectCreationFlow ? (
                  <div className="geodata-settings-page__back-slot">
                    <BackArrow
                      onClick={handleSaveGeodata}
                      disabled={hasGeodataInputValidationError || isSaving}
                      aria-label="Back"
                    />
                  </div>
                ) : null}
                <div className="geodata-settings-page__scroll-content">
                  {isCloneFlow && cloneSelections ? (
                    <div className="geodata-clone-config">
                      <div className="geodata-clone-config__title">Geodata clone mode</div>
                      {(['terrain', 'buildings', 'trees'] as CloneModuleKey[]).map((module) => {
                        const selection = cloneSelections[module];
                        const canCloneModule =
                          cloneAvailability.sourceSucceeded &&
                          ((module === 'terrain' && cloneAvailability.hasTerrain) ||
                            (module === 'buildings' && cloneAvailability.hasBuildings) ||
                            (module === 'trees' && cloneAvailability.hasTrees));
                        const cloneTooltip = !cloneAvailability.sourceSucceeded
                          ? 'Clone is unavailable because the source geodata has not completed successfully.'
                          : 'Clone is unavailable because the source geodata does not contain this module.';

                        return (
                          <div key={module} className="geodata-clone-config__row">
                            <FormControlLabel
                              className="checkbox-row"
                              control={
                                <Checkbox
                                  checked={selection.enabled}
                                  disabled={module === 'terrain'}
                                  onChange={(event) => handleCloneSelectionToggle(module, event.target.checked)}
                                />
                              }
                              label={CLONE_MODULE_LABELS[module]}
                            />

                            <RadioGroup
                              row
                              value={selection.mode}
                              onChange={(event) =>
                                handleCloneModeChange(module, event.target.value as GeodataCloneModuleSelection['mode'])
                              }
                              className="geodata-clone-config__modes"
                            >
                              <FormControlLabel
                                className="radio-row"
                                value="rerun"
                                control={<Radio />}
                                disabled={!selection.enabled}
                                label="Rerun"
                              />
                              <span className="geodata-clone-config__clone-option">
                                <FormControlLabel
                                  className="radio-row"
                                  value="clone"
                                  control={<Radio />}
                                  disabled={!selection.enabled || !canCloneModule}
                                  label="Clone"
                                />
                                {!canCloneModule ? <CustomTooltip title={cloneTooltip} /> : null}
                              </span>
                            </RadioGroup>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {visibleSections.map((section) => {
                    const isOpen = activeSection === section;

                    return (
                      <div key={section} className="geodata-accordion">
                        <button
                          type="button"
                          className={`geodata-accordion__trigger ${isOpen ? 'geodata-accordion__trigger--open' : ''}${
                            sectionHasError[section] ? ' geodata-accordion__trigger--error' : ''
                          }`}
                          onClick={() => handleSectionToggle(section)}
                        >
                          <span className="geodata-accordion__icon">{isOpen ? '▾' : '▸'}</span>
                          <span className="geodata-slider-field__label--with-info">
                            <span>{SECTION_LABELS[section]}</span>
                            {section === 'uploads' ? <CustomTooltip title={uploadCustomObjectsTooltip} arrow /> : null}
                          </span>
                        </button>

                        {isOpen && (
                          <div className="geodata-accordion__content">
                            {section === 'terrain' && (
                              <Terrain
                                readOnly={isReadOnlyReview}
                                closeTerrainDisabled={!isReadOnlyReview && (!isProjectCreationFlow || isCloneFlow)}
                              />
                            )}
                            {section === 'buildings' && (
                              <Buildings readOnly={isReadOnlyReview} hideIncludeToggle={isCloneFlow} />
                            )}
                            {section === 'trees' && (
                              <Trees
                                readOnly={isReadOnlyReview}
                                storageImportDisabled={isStorageLimitReachedForImports}
                                hideIncludeToggle={isCloneFlow}
                              />
                            )}
                            {section === 'uploads' && (
                              <UploadCustomObjects
                                projectId={projectId}
                                readOnly={isReadOnlyReview}
                                storageImportDisabled={isStorageLimitReachedForImports}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            }
            right={
              usesExternalPreview ? (
                <div ref={previewSlotRef} className="grid-settings-step__preview-slot" />
              ) : (
                <MapPreviewModeFrame
                  gridId={gridId}
                  isPreviewLoading={previewLoading || isStandaloneGeodataPollingEnabled}
                  onUpdatePreview={
                    gridId
                      ? typeof onUpdatePreview === 'function'
                        ? () => onUpdatePreview(String(gridId))
                        : handleUpdateStandalonePreview
                      : null
                  }
                  hasUnsavedChanges={
                    !isProjectCreationFlow &&
                    lastSavedPreviewSignature !== null &&
                    lastSavedPreviewSignature !== currentPreviewSignature
                  }
                  hasValidationErrors={hasGeodataInputValidationError}
                >
                  <CadastralMapSection
                    isReview={false}
                    readOnly={true}
                    allowAnalysisAreaAngleCopy={true}
                    isParcelInteractionDisabled={true}
                    mapType={mapType}
                    mapViews={mapViews}
                    setMapType={setMapType}
                    setMapViews={setMapViews}
                    mapCenter={mapCenter}
                    crs2056={crs2056}
                    gridParams={gridParams}
                    Style={Style}
                    legalBorderRef={legalBorderRef}
                    buildableBorderRef={buildableBorderRef}
                    analysisAreaRef={analysisAreaRef}
                    analysisAreaInactiveRefs={analysisAreaInactiveRefs}
                    showOnlyAnalysisAreas
                    inactiveAnalysisAreaStyle={Style.analysisArea}
                    isDrawingRef={isDrawingRef}
                    showEditableCoordinatesArea={false}
                    AnalysisAreaCenterMarkerSync={ReadOnlyAnalysisAreaCenterMarkerSync}
                    cadastralOpacity={Number(Settings[import.meta.env.MODE as keyof typeof Settings].cadastral_opacity)}
                    focusedPolygonRef={null}
                    coordinateSystem={defaultCoordinateSystemPreference}
                    cadastralLayerKey={0}
                  />
                </MapPreviewModeFrame>
              )
            }
          />
        </div>

        {!isReadOnlyReview &&
          (isProjectCreationFlow ? (
            <ExitDialog
              open={exitModalOpen}
              onClose={() => setExitModalOpen(false)}
              mode="confirm"
              onCancel={() => setExitModalOpen(false)}
              onConfirm={() => {
                void handleSaveAndExit();
              }}
              isBusy={isSaving}
            />
          ) : (
            <ExitDialog
              open={exitModalOpen}
              onClose={() => setExitModalOpen(false)}
              mode="confirm"
              onCancel={() => setExitModalOpen(false)}
              onConfirm={() => {
                setExitModalOpen(false);
                handleExit();
              }}
              isBusy={isSaving}
            />
          ))}
      </ProjectCreationShell>

      {!isReadOnlyReview &&
        (!isProjectCreationFlow ? (
          <nav className="onboarding-nav onboarding-nav--fixed">
            <div className="onboarding-nav-left" />
            <div className="onboarding-nav-right">
              <UMButton
                text={isSaving ? 'SAVING...' : 'CREATE'}
                onClick={handleSaveGeodata}
                icon={ArrowRight}
                customClassName="onboarding-finish-button onboarding-finish-button--primary"
                disabled={isSaving || hasGeodataInputValidationError}
                loading={isSaving}
              />
            </div>
          </nav>
        ) : null)}
    </>
  );
};

export default AddGeodata;
